import { Dispatch, FC, RefObject, SetStateAction, useEffect } from 'react'
import { flushSync } from 'react-dom'

import { getLeadFieldValue } from '../../utils/leadFields'
import { useLeadRefetch } from './LeadRefetchContext'

import AddFunnelToLeadsModal from '../AddFunnelToLeadsModal/AddFunnelToLeadsModal'
import RemoveFunnelFromLeadsModal from '../RemoveFunnelFromLeadsModal/RemoveFunnelFromLeadsModal'
import AddDealToLeadModal from '../AddDealToLeadModal/AddDealToLeadModal'
import RemoveDealFromLeadModal from '../RemoveDealFromLeadModal/RemoveDealFromLeadModal'
import EditLeadNameModal from './components/EditLeadNameModal/EditLeadNameModal'
import DeleteLeadModal from '../DeleteLeadModal/DeleteLeadModal'
import LeadFunnelsBulkChangeManagerModal from '../LeadFunnelsBulkChangeManagerModal/LeadFunnelsBulkChangeManagerModal'
import LeadFunnelsBulkChangeStateModal from '../LeadFunnelsBulkChangeStateModal/LeadFunnelsBulkChangeStateModal'
import AddStudAccountModal from './components/TabBindingStudAccount/AddStudAccountModal'
import { useAddAlert } from '@/hooks/useAddAlert'

import type { LeadState } from '@/api/types/crm/crm.types'

import type {
  LeadProfileLead,
  LeadProfileModalContentRef,
  OpenModalType,
} from './LeadProfileModal.types'
import { toValidFunnelId } from './leadModalRefetchMerge'

interface LeadProfileModalModalsProps {
  activeLead: LeadProfileLead
  openModal: { type: OpenModalType }
  setOpenModal: Dispatch<SetStateAction<{ type: OpenModalType }>>
  leadNewType: { key: any; name: string } | null
  modalContentRef: RefObject<LeadProfileModalContentRef | null>
  setActiveLead: Dispatch<SetStateAction<LeadProfileLead>>
  onCloseLeadModalWithRefetch: () => void
}

const LeadProfileModalModals: FC<LeadProfileModalModalsProps> = ({
  activeLead,
  openModal,
  setOpenModal,
  leadNewType,
  modalContentRef,
  setActiveLead,
  onCloseLeadModalWithRefetch,
}) => {
  const addAlert = useAddAlert()
  const {
    handleRefetchLeadInModal,
    refetchLeadHardAfterMutation,
    handleRefetchLeadsOnClose,
    patchLead,
    selectedFunnelId,
    selectLeadFunnel,
    selectedLeadFunnelId,
    leadCardSnapshot,
    initialFunnelIdForRefetch,
    setInitialFunnelIdForRefetch,
  } = useLeadRefetch()

  const refetchFunnelIdForLeadCard =
    toValidFunnelId(selectedFunnelId) ??
    toValidFunnelId(activeLead?.currentFunnelId) ??
    toValidFunnelId(initialFunnelIdForRefetch)

  const hasRealFunnel = (activeLead?.funnels ?? []).some((funnel) => {
    const id = Number(funnel?.id)
    return Number.isFinite(id) && id > 0
  })

  const pickNextLeadFunnelAfterRemove = (removedLeadFunnelId: number) => {
    const currentFunnels = leadCardSnapshot?.current_funnels
    const list = Array.isArray(currentFunnels) ? currentFunnels : []
    const remaining = list.filter((row) => {
      const id = Number(row.lead_funnel_id ?? row.id)
      return Number.isFinite(id) && id > 0 && id !== removedLeadFunnelId
    })
    const next = remaining[0]
    if (!next) return null
    return {
      leadFunnelId: Number(next.lead_funnel_id ?? next.id),
      funnelId: Number(next.funnel_id),
    }
  }

  useEffect(() => {
    if (openModal.type !== 'change-lead-state' || leadNewType?.key !== 'in_work') return
    if (hasRealFunnel) return
    addAlert({
      title: 'Ошибка',
      text: 'Сначала добавьте воронку',
      type: 'error',
    })
    setOpenModal({ type: null })
  }, [addAlert, hasRealFunnel, leadNewType?.key, openModal.type, setOpenModal])

  return (
    <>
      {openModal.type === 'add-funnel' && (
        <AddFunnelToLeadsModal
          lead={activeLead}
          open
          onClose={() => setOpenModal({ type: null })}
          onSuccess={async () => {
            modalContentRef.current?.refreshHistory()
            setOpenModal({ type: null })
            await handleRefetchLeadInModal(refetchFunnelIdForLeadCard)
            handleRefetchLeadsOnClose()
          }}
        />
      )}

      {openModal.type === 'add-deal' && (
        <AddDealToLeadModal
          lead={activeLead}
          open
          onClose={() => setOpenModal({ type: null })}
          onSuccess={async ({ leadFunnelId, funnelId }) => {
            modalContentRef.current?.refreshHistory()
            setOpenModal({ type: null })
            selectLeadFunnel(leadFunnelId, { funnelId })
            handleRefetchLeadsOnClose()
          }}
        />
      )}

      <RemoveFunnelFromLeadsModal
        lead={activeLead}
        open={openModal.type === 'remove-funnel'}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={async () => {
          modalContentRef.current?.refreshHistory()
          setOpenModal({ type: null })
          setActiveLead((prev) =>
            prev
              ? {
                  ...prev,
                  currentFunnelId: null,
                  funnel: null,
                  funnels: [],
                  currentFunnelStatusId: null,
                  currentFunnelStatusName: null,
                  currentFunnelStatusColor: null,
                }
              : prev,
          )
          setInitialFunnelIdForRefetch(null)
          await handleRefetchLeadInModal(null)
          handleRefetchLeadsOnClose()
        }}
      />

      <RemoveDealFromLeadModal
        lead={activeLead}
        open={openModal.type === 'remove-deal'}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={async ({ removedLeadFunnelId }) => {
          modalContentRef.current?.refreshHistory()
          setOpenModal({ type: null })

          const wasActive =
            selectedLeadFunnelId != null &&
            Number(selectedLeadFunnelId) === Number(removedLeadFunnelId)
          const next = wasActive
            ? pickNextLeadFunnelAfterRemove(removedLeadFunnelId)
            : null

          if (next) {
            selectLeadFunnel(next.leadFunnelId, { funnelId: next.funnelId })
          } else {
            await handleRefetchLeadInModal(refetchFunnelIdForLeadCard)
          }
          handleRefetchLeadsOnClose()
        }}
      />

      <EditLeadNameModal
        open={openModal.type === 'edit-name'}
        name={getLeadFieldValue(activeLead, 'NAME') ?? ''}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={(name) => {
          modalContentRef.current?.handleChangeFieldData('NAME', name)
          setOpenModal({ type: null })
        }}
      />

      <DeleteLeadModal
        keyTable={activeLead?.type!}
        lead={activeLead}
        forceHardDelete
        open={openModal.type === 'delete-lead'}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={() => {
          setOpenModal({ type: null })
          onCloseLeadModalWithRefetch()
        }}
      />

      {openModal.type === 'change-lead-state' &&
        leadNewType?.key === 'in_work' &&
        hasRealFunnel && (
        <LeadFunnelsBulkChangeManagerModal
          items={[
            {
              id: activeLead?.lead_funnel_id as number,
              name: getLeadFieldValue(activeLead, 'NAME') || '',
            },
          ]}
          leadStateType={'in_work'}
          open
          onClose={() => setOpenModal({ type: null })}
          onSuccess={async () => {
            setOpenModal({ type: null })
            flushSync(() => {
              patchLead({ type: 'in_work' })
            })
            await handleRefetchLeadInModal(refetchFunnelIdForLeadCard)
            handleRefetchLeadsOnClose()
          }}
        />
      )}

      {(openModal.type === 'change-lead-state-in-client' ||
        (openModal.type === 'change-lead-state' &&
          leadNewType?.key === 'processed')) && (
        <LeadFunnelsBulkChangeStateModal
          items={
            activeLead?.lead_funnel_id
              ? [
                  {
                    id: activeLead.lead_funnel_id,
                    name: getLeadFieldValue(activeLead, 'NAME') || '',
                  },
                ]
              : []
          }
          targetType={
            openModal.type === 'change-lead-state-in-client' ? 'client' : 'processed'
          }
          open
          onClose={() => setOpenModal({ type: null })}
          onSuccess={async () => {
            const nextType: LeadState =
              openModal.type === 'change-lead-state-in-client'
                ? 'client'
                : 'processed'
            flushSync(() => {
              patchLead({ type: nextType })
            })
            setOpenModal({ type: null })
            await handleRefetchLeadInModal(refetchFunnelIdForLeadCard)
            handleRefetchLeadsOnClose()
          }}
        />
      )}

      <AddStudAccountModal
        lead={activeLead}
        open={openModal.type === 'add-stud-account'}
        onClose={() => setOpenModal({ type: null })}
        onSuccess={async (userId) => {
          setOpenModal({ type: null })
          flushSync(() => {
            patchLead({ user_id: userId })
          })
          modalContentRef.current?.refreshHistory()
          await refetchLeadHardAfterMutation()
          handleRefetchLeadsOnClose()
        }}
      />
    </>
  )
}

export default LeadProfileModalModals

